import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { 
  Calendar, Play, X, ChevronDown, Heart, 
  MessageCircle, ExternalLink, ArrowRight, Film, BookOpen, Clock
} from "lucide-react";
import { API, BACKEND_URL } from "../App";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogTitle } from "../components/ui/dialog";

const SDE_TYPE_LABELS = {
  groom: "Groom's Story",
  bride: "Bride's Story",
  best_man: "Best Man Moments",
  maid_of_honor: "Maid of Honor Memories",
};

export default function WeddingPage() {
  const { slug } = useParams();
  const [wedding, setWedding] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [treeSubmissions, setTreeSubmissions] = useState([]);
  const [sdeProjects, setSdeProjects] = useState([]);
  const [hasStory, setHasStory] = useState(false);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [lightboxImage, setLightboxImage] = useState(null);

  useEffect(() => {
    const fetchWedding = async () => {
      try {
        const response = await axios.get(`${API}/weddings/slug/${slug}`);
        const w = response.data;
        setWedding(w);
        
        const [treeRes, sdeRes] = await Promise.all([
          axios.get(`${API}/tree/${w.wedding_id}`).catch(() => ({ data: [] })),
          axios.get(`${API}/sde/public/${w.wedding_id}`).catch(() => ({ data: [] })),
        ]);
        setTreeSubmissions(treeRes.data);
        setSdeProjects(sdeRes.data);

        // Check if there's a published story
        try {
          await axios.get(`${API}/stories/public/${w.wedding_id}`);
          setHasStory(true);
        } catch { setHasStory(false); }
      } catch (err) {
        setError(err.response?.status === 404 ? "Wedding not found" : "Something went wrong");
      } finally {
        setLoading(false);
      }
    };
    fetchWedding();
  }, [slug]);

  const getMediaUrl = (url) => {
    if (!url) return null;
    return url.startsWith("http") ? url : `${BACKEND_URL}${url}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-wedding-black flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-2 border-wedding-gold border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-wedding-ivory/60 font-light">Loading your memories...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-wedding-black flex items-center justify-center px-6">
        <div className="text-center">
          <Heart className="w-16 h-16 text-wedding-gold/30 mx-auto mb-6" />
          <h1 className="font-serif text-3xl text-wedding-ivory mb-4">{error}</h1>
          <p className="text-wedding-ivory/60 mb-8">This wedding page may not exist or is private.</p>
          <Link to="/" className="btn-secondary">Return Home</Link>
        </div>
      </div>
    );
  }

  const sections = wedding.sections_enabled || {};
  const features = wedding.features || {};
  const coupleName = `${wedding.couple_name_1} & ${wedding.couple_name_2}`;
  const previewCount = wedding.best_wishes_preview_count || 6;
  const isOurStoryMode = wedding.landing_page_mode === "our_story" || features.our_story_mode;
  const timeline = wedding.timeline_events || [];

  return (
    <div className="min-h-screen bg-wedding-black" data-testid="wedding-page">
      {/* Hero Section */}
      {sections.hero !== false && (
        <section className="hero-section relative min-h-screen">
          {wedding.hero_image ? (
            <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${getMediaUrl(wedding.hero_image)})` }} />
          ) : (
            <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: "url('https://images.unsplash.com/photo-1519741497674-611481863552?auto=format&fit=crop&w=2000&q=80')" }} />
          )}
          <div className="hero-overlay"></div>
          
          <motion.div className="hero-content px-6 z-10 relative" initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, delay: 0.3 }}>
            <p className="text-wedding-gold text-sm tracking-[0.3em] uppercase mb-6">{wedding.wedding_date || "A Celebration of Love"}</p>
            <h1 className="font-serif text-5xl md:text-7xl lg:text-8xl tracking-tight text-wedding-ivory mb-6" data-testid="couple-names">
              {wedding.couple_name_1}<span className="text-wedding-gold mx-4">&</span>{wedding.couple_name_2}
            </h1>
            {wedding.subtitle_text && (
              <p className="text-wedding-ivory/70 text-lg md:text-xl max-w-2xl mx-auto font-light font-accent italic">{wedding.subtitle_text}</p>
            )}
          </motion.div>

          <motion.div className="absolute bottom-12 left-1/2 transform -translate-x-1/2" animate={{ y: [0, 10, 0] }} transition={{ duration: 2, repeat: Infinity }}>
            <ChevronDown className="w-8 h-8 text-wedding-gold/50" />
          </motion.div>
        </section>
      )}

      {/* Timeline Section — "Our Story" Mode */}
      {isOurStoryMode && sections.timeline !== false && timeline.length > 0 && (
        <section className="py-24 px-6 md:px-12 lg:px-24" data-testid="timeline-section">
          <div className="max-w-4xl mx-auto">
            <motion.div className="text-center mb-16" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
              <p className="text-wedding-gold text-sm tracking-[0.3em] uppercase mb-4">Our Journey</p>
              <h2 className="section-title">Our Story</h2>
            </motion.div>

            <div className="relative">
              {/* Timeline line */}
              <div className="absolute left-6 md:left-1/2 top-0 bottom-0 w-px bg-wedding-gold/20" />

              {timeline.map((event, index) => (
                <motion.div
                  key={index}
                  className={`relative flex items-start mb-12 ${index % 2 === 0 ? "md:flex-row" : "md:flex-row-reverse"}`}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  {/* Dot */}
                  <div className="absolute left-6 md:left-1/2 transform -translate-x-1/2 w-3 h-3 rounded-full bg-wedding-gold z-10 mt-2" />
                  
                  <div className={`ml-14 md:ml-0 md:w-1/2 ${index % 2 === 0 ? "md:pr-12 md:text-right" : "md:pl-12"}`}>
                    {event.image && (
                      <div className="aspect-video mb-4 overflow-hidden rounded-sm">
                        <img src={getMediaUrl(event.image)} alt={event.title} className="w-full h-full object-cover" />
                      </div>
                    )}
                    <p className="text-wedding-gold text-sm mb-1 font-mono">{event.date}</p>
                    <h3 className="font-serif text-xl text-wedding-ivory mb-2">{event.title}</h3>
                    <p className="text-wedding-ivory/60 text-sm leading-relaxed">{event.description}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Same Day Edit Video Section */}
      {sections.highlights !== false && wedding.sde_video && (
        <section className="py-24 px-6 md:px-12 lg:px-24">
          <div className="max-w-5xl mx-auto">
            <motion.div className="text-center mb-16" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
              <p className="text-wedding-gold text-sm tracking-[0.3em] uppercase mb-4">Highlights</p>
              <h2 className="section-title">Same Day Edit</h2>
            </motion.div>
            <motion.div className="video-container rounded-sm overflow-hidden" initial={{ opacity: 0, scale: 0.95 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }}>
              <video src={getMediaUrl(wedding.sde_video)} controls className="w-full h-full" poster={wedding.hero_image ? getMediaUrl(wedding.hero_image) : undefined} />
            </motion.div>
          </div>
        </section>
      )}

      {/* Wedding Perspectives Section (Personal SDE) — only show projects with clips */}
      {features.personal_sde && sections.perspectives !== false && sdeProjects.filter(p => p.clips?.length > 0).length > 0 && (
        <section className="py-24 px-6 md:px-12 lg:px-24 bg-wedding-charcoal" data-testid="perspectives-section">
          <div className="max-w-6xl mx-auto">
            <motion.div className="text-center mb-16" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
              <p className="text-wedding-gold text-sm tracking-[0.3em] uppercase mb-4">Perspectives</p>
              <h2 className="section-title">Wedding Perspectives</h2>
              <p className="text-wedding-ivory/60 mt-4 max-w-xl mx-auto">Different eyes, same beautiful day</p>
            </motion.div>

            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {sdeProjects.filter(p => p.clips?.length > 0).map((project, index) => (
                <motion.div
                  key={project.sde_id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Link
                    to={`/${slug}/sde/${project.sde_type}`}
                    className="block p-8 bg-wedding-subtle border border-white/5 hover:border-wedding-gold/30 transition-all group text-center"
                    data-testid={`sde-card-${project.sde_type}`}
                  >
                    <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-wedding-gold/10 flex items-center justify-center group-hover:bg-wedding-gold/20 transition-colors">
                      <Film className="w-8 h-8 text-wedding-gold" />
                    </div>
                    <h3 className="font-serif text-lg text-wedding-ivory mb-1">{SDE_TYPE_LABELS[project.sde_type] || project.sde_type}</h3>
                    <p className="text-wedding-gold text-sm">{project.participant_name}</p>
                    <div className="mt-4 flex items-center justify-center gap-1 text-wedding-gold/60 group-hover:text-wedding-gold text-sm transition-colors">
                      <Play className="w-3 h-3" />
                      <span>Watch</span>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Interactive Story Section — only show if story has a video or interaction points */}
      {features.interactive_story && sections.interactive_story !== false && hasStory && (
        <section className="py-24 px-6 md:px-12 lg:px-24" data-testid="story-section">
          <div className="max-w-4xl mx-auto text-center">
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
              <p className="text-wedding-gold text-sm tracking-[0.3em] uppercase mb-4">Interactive</p>
              <h2 className="section-title mb-6">Our Story</h2>
              <p className="text-wedding-ivory/60 max-w-lg mx-auto mb-10">
                How well do you know this love story? Answer questions and discover their journey together.
              </p>
              <Link
                to={`/${slug}/story`}
                className="btn-primary inline-flex items-center gap-3"
                data-testid="play-story-btn"
              >
                <BookOpen className="w-5 h-5" />
                Play Experience
              </Link>
            </motion.div>
          </div>
        </section>
      )}

      {/* Wedding Tree Preview Section */}
      {sections.tree !== false && wedding.tree_enabled !== false && (
        <section className="py-24 px-6 md:px-12 lg:px-24 bg-wedding-charcoal">
          <div className="max-w-6xl mx-auto">
            <motion.div className="text-center mb-16" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
              <p className="text-wedding-gold text-sm tracking-[0.3em] uppercase mb-4">Messages</p>
              <h2 className="section-title">Wedding Tree</h2>
              <p className="text-wedding-ivory/60 mt-4 max-w-xl mx-auto">Heartfelt messages from our loved ones</p>
            </motion.div>

            <div className="relative mb-12">
              {treeSubmissions.length === 0 ? (
                <div className="text-center py-16">
                  <MessageCircle className="w-16 h-16 text-wedding-gold/30 mx-auto mb-4" />
                  <p className="text-wedding-ivory/60">No messages yet. Be the first to leave a greeting!</p>
                </div>
              ) : (
                <div className="relative w-full min-h-[300px] flex items-center justify-center">
                  {treeSubmissions.slice(0, 8).map((submission, index) => {
                    const angle = (index / Math.min(treeSubmissions.length, 8)) * Math.PI * 2;
                    const radius = 100 + (index % 3) * 40;
                    const x = Math.cos(angle) * radius;
                    const y = Math.sin(angle) * radius;
                    const isFeatured = submission.status === "featured";
                    return (
                      <motion.div
                        key={submission.submission_id}
                        className={`orb ${isFeatured ? "orb-featured" : ""}`}
                        style={{ left: `calc(50% + ${x}px - 30px)`, top: `calc(50% + ${y}px - 30px)` }}
                        initial={{ opacity: 0, scale: 0 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        viewport={{ once: true }}
                        transition={{ delay: index * 0.1 }}
                        onClick={() => setSelectedSubmission(submission)}
                        whileHover={{ scale: 1.2 }}
                      >
                        {(submission.photo_url || submission.thumbnail_url) ? (
                          <img src={getMediaUrl(submission.photo_url || submission.thumbnail_url)} alt={submission.guest_name || "Guest"} className="w-full h-full object-cover rounded-full" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <span className="text-wedding-black font-bold">{(submission.guest_name || "?")[0].toUpperCase()}</span>
                          </div>
                        )}
                      </motion.div>
                    );
                  })}
                  <div className="absolute w-16 h-16 rounded-full border-2 border-wedding-gold/30 flex items-center justify-center">
                    <Heart className="w-6 h-6 text-wedding-gold" />
                  </div>
                </div>
              )}
            </div>

            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Link to={`/${slug}/tree`} className="btn-secondary inline-flex items-center gap-2" data-testid="view-tree-btn">
                View Full Tree <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to={`/tree/submit/${wedding.wedding_id}`} className="btn-primary inline-flex items-center gap-2" data-testid="submit-greeting-btn">
                <MessageCircle className="w-4 h-4" /> Leave a Greeting
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Wedding Vows Section */}
      {sections.vows !== false && (wedding.groom_vow_text || wedding.bride_vow_text) && (
        <section className="py-24 px-6 md:px-12 lg:px-24">
          <div className="max-w-5xl mx-auto">
            <motion.div className="text-center mb-16" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
              <p className="text-wedding-gold text-sm tracking-[0.3em] uppercase mb-4">From the Heart</p>
              <h2 className="section-title">Our Vows</h2>
            </motion.div>
            <div className="grid md:grid-cols-2 gap-12">
              {wedding.groom_vow_text && (
                <motion.div className="text-center" initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
                  <h3 className="font-serif text-2xl text-wedding-gold mb-6">{wedding.couple_name_1}</h3>
                  <blockquote className="font-accent text-xl text-wedding-ivory/90 italic leading-relaxed">"{wedding.groom_vow_text}"</blockquote>
                </motion.div>
              )}
              {wedding.bride_vow_text && (
                <motion.div className="text-center" initial={{ opacity: 0, x: 20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
                  <h3 className="font-serif text-2xl text-wedding-gold mb-6">{wedding.couple_name_2}</h3>
                  <blockquote className="font-accent text-xl text-wedding-ivory/90 italic leading-relaxed">"{wedding.bride_vow_text}"</blockquote>
                </motion.div>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Best Wishes */}
      {sections.best_wishes !== false && treeSubmissions.length > 0 && (
        <section className="py-24 px-6 md:px-12 lg:px-24 bg-wedding-charcoal">
          <div className="max-w-6xl mx-auto">
            <motion.div className="text-center mb-16" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
              <p className="text-wedding-gold text-sm tracking-[0.3em] uppercase mb-4">Messages</p>
              <h2 className="section-title">Best Wishes</h2>
            </motion.div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {treeSubmissions.slice(0, previewCount).map((submission, index) => (
                <motion.div
                  key={submission.submission_id}
                  className="relative aspect-video bg-wedding-subtle rounded-sm overflow-hidden cursor-pointer group"
                  initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                  transition={{ delay: index * 0.05 }}
                  onClick={() => setSelectedSubmission(submission)}
                >
                  {(submission.photo_url || submission.thumbnail_url) ? (
                    <img src={getMediaUrl(submission.photo_url || submission.thumbnail_url)} alt={submission.guest_name || "Guest"} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-wedding-gold/20 to-wedding-gold/5">
                      <MessageCircle className="w-12 h-12 text-wedding-gold/50" />
                    </div>
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-wedding-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                    <div className="absolute bottom-3 left-3 right-3">
                      <p className="text-wedding-ivory text-sm font-medium truncate">{submission.guest_name || "A Guest"}</p>
                    </div>
                  </div>
                  {submission.video_url && (
                    <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity">
                      <div className="w-12 h-12 rounded-full bg-wedding-gold/90 flex items-center justify-center">
                        <Play className="w-5 h-5 text-wedding-black ml-1" />
                      </div>
                    </div>
                  )}
                </motion.div>
              ))}
            </div>
            {treeSubmissions.length > previewCount && (
              <div className="text-center mt-8">
                <Link to={`/${slug}/tree`} className="text-wedding-gold hover:underline inline-flex items-center gap-2">
                  View all {treeSubmissions.length} messages <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Photo Gallery Section */}
      {sections.gallery !== false && wedding.gallery_images?.length > 0 && (
        <section className="py-24 px-6 md:px-12 lg:px-24">
          <div className="max-w-7xl mx-auto">
            <motion.div className="text-center mb-16" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
              <p className="text-wedding-gold text-sm tracking-[0.3em] uppercase mb-4">Gallery</p>
              <h2 className="section-title">Moments Captured</h2>
            </motion.div>
            <div className="gallery-grid">
              {wedding.gallery_images.map((image, index) => (
                <motion.div key={index} className="gallery-item cursor-pointer"
                  initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                  transition={{ delay: index * 0.05 }}
                  onClick={() => setLightboxImage(image)} data-testid={`gallery-image-${index}`}
                >
                  <img src={getMediaUrl(image)} alt={`Wedding moment ${index + 1}`} />
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Gratitude Section */}
      {sections.gratitude !== false && (wedding.gratitude_video || wedding.gratitude_text) && (
        <section className="py-24 px-6 md:px-12 lg:px-24 bg-wedding-charcoal">
          <div className="max-w-4xl mx-auto">
            <motion.div className="text-center mb-16" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
              <p className="text-wedding-gold text-sm tracking-[0.3em] uppercase mb-4">Thank You</p>
              <h2 className="section-title">Our Gratitude</h2>
            </motion.div>
            {wedding.gratitude_video && (
              <motion.div className="video-container rounded-sm overflow-hidden mb-8" initial={{ opacity: 0, scale: 0.95 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }}>
                <video src={getMediaUrl(wedding.gratitude_video)} controls className="w-full h-full" poster={wedding.hero_image ? getMediaUrl(wedding.hero_image) : undefined} />
              </motion.div>
            )}
            {wedding.gratitude_text && (
              <motion.div className="text-center" initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }}>
                <blockquote className="font-accent text-2xl text-wedding-ivory/90 italic leading-relaxed max-w-2xl mx-auto">"{wedding.gratitude_text}"</blockquote>
                <p className="text-wedding-gold mt-6">— {coupleName}</p>
              </motion.div>
            )}
          </div>
        </section>
      )}

      {/* Footer */}
      <footer className="py-16 px-6 border-t border-white/5">
        <div className="max-w-7xl mx-auto text-center">
          <Heart className="w-8 h-8 text-wedding-gold mx-auto mb-4" />
          <h3 className="font-serif text-2xl text-wedding-ivory mb-2">{coupleName}</h3>
          <p className="text-wedding-ivory/40 text-sm mb-8">{wedding.wedding_date}</p>
          <div className="border-t border-white/5 pt-8">
            <p className="text-wedding-gold/60 text-sm">MemoraOrbs</p>
            <p className="text-wedding-ivory/30 text-xs italic mt-1">Memories You Can Hold</p>
          </div>
        </div>
      </footer>

      {/* Tree Submission Modal */}
      <Dialog open={!!selectedSubmission} onOpenChange={() => setSelectedSubmission(null)}>
        <DialogContent className="bg-wedding-charcoal border-white/10 max-w-lg">
          <DialogTitle className="font-serif text-xl text-wedding-ivory">{selectedSubmission?.guest_name || "A Guest"}</DialogTitle>
          {selectedSubmission?.video_url && (
            <div className="video-container mt-4">
              <video src={getMediaUrl(selectedSubmission.video_url)} controls className="w-full rounded-sm" />
            </div>
          )}
          {selectedSubmission?.message && (
            <p className="text-wedding-ivory/80 mt-4 font-accent italic">"{selectedSubmission.message}"</p>
          )}
        </DialogContent>
      </Dialog>

      {/* Image Lightbox */}
      <AnimatePresence>
        {lightboxImage && (
          <motion.div className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={() => setLightboxImage(null)}
          >
            <button className="absolute top-6 right-6 text-white/60 hover:text-white" onClick={() => setLightboxImage(null)}>
              <X className="w-8 h-8" />
            </button>
            <motion.img src={getMediaUrl(lightboxImage)} alt="Gallery" className="max-w-full max-h-[90vh] object-contain"
              initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.9, opacity: 0 }}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
